<template>
  <div class="notification-settings">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" color="white">mdi-bell-ring</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">{{ $t("notification_settings") }}</h1>
          <p class="header-subtitle">Customize how you receive notifications</p>
        </div>
      </div>
      <div class="header-decoration"></div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="48"
          width="4"
        />
        <p class="loading-text">Loading your preferences...</p>
      </div>
    </div>

    <!-- Settings Cards -->
    <div v-else class="settings-container">
  <div class="settings-grid">
    <div
      v-for="(item) in menuList"
      :key="item.key"
      class="setting-card"
      :class="{ 'expanded': expandedItems.includes(item.key) }"
    >
      <!-- Card Header (move click handler here) -->
      <div class="card-header" @click="toggleExpanded(item.key)">
        <div class="card-icon">
          <v-icon :color="getIconColor(item.key)" size="24">
            {{ getIconName(item.key) }}
          </v-icon>
        </div>
        <div class="card-title-section">
          <h3 class="card-title">{{ item.title }}</h3>
          <p class="card-description">{{ item.description }}</p>
        </div>
        <div class="expand-indicator">
          <v-icon 
            class="expand-icon" 
            :class="{ 'rotated': expandedItems.includes(item.key) }"
          >
            mdi-chevron-down
          </v-icon>
        </div>
      </div>

      <!-- Card Content -->
      <div class="card-content" v-show="expandedItems.includes(item.key)">
        <div class="notification-options">
          <!-- Push Notification -->
          <div 
            v-if="item.key !== 'messages'" 
            class="notification-option"
          >
            <div class="option-info">
              <div class="option-icon push">
                <v-icon size="20" color="white">mdi-bell</v-icon>
              </div>
              <div class="option-details">
                <span class="option-label">Push Notifications</span>
                <span class="option-sublabel">Instant alerts on your device</span>
              </div>
            </div>
            <div class="option-toggle">
              <v-switch
                v-model="item.push"
                hide-details
                color="primary"
                class="custom-switch"
                @change="onChange(item, 'push')"
                @click.stop
              />
            </div>
          </div>

          <!-- Email Notification -->
          <div class="notification-option">
            <div class="option-info">
              <div class="option-icon email">
                <v-icon size="20" color="white">mdi-email</v-icon>
              </div>
              <div class="option-details">
                <span class="option-label">Email Notifications</span>
                <span class="option-sublabel">Receive updates in your inbox</span>
              </div>
            </div>
            <div class="option-toggle">
              <v-switch
                v-model="item.email"
                hide-details
                color="primary"
                class="custom-switch"
                @change="onChange(item, 'email')"
                @click.stop
              />
            </div>
          </div>

          <!-- SMS Notification - Hidden for future use -->
          <div v-if="false" class="notification-option">
            <div class="option-info">
              <div class="option-icon sms">
                <v-icon size="20" color="white">mdi-message</v-icon>
              </div>
              <div class="option-details">
                <span class="option-label">SMS Notifications</span>
                <span class="option-sublabel">Text messages to your phone</span>
              </div>
            </div>
            <div class="option-toggle">
              <v-switch
                v-model="item.sms"
                hide-details
                color="primary"
                class="custom-switch"
                @change="onChange(item, 'sms')"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      menuList: [
        {
          title: "Messages",
          description: "Notifications about messages from volunteers and agencies",
          key: "messages",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Posts",
          description: "Updates about new event posts and modifications",
          key: "posts",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Comments",
          description: "Comments and reactions on your event posts",
          key: "comments",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Needs",
          description: "Your needs and applications you've submitted",
          key: "needs",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Rewards",
          description: "Your rewards and rewards for your needs",
          key: "rewards",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Follows",
          description: "New followers and fans notifications",
          key: "follows",
          push: true,
          email: true,
          sms: true
        },
        {
          title: "Newsletter",
          description: "Newsletters and important announcements",
          key: "newsletters",
          push: true,
          email: true,
          sms: true
        }
      ],
      loading: false,
      expandedItems: []
    };
  },
  methods: {
    ...mapActions("usersetting", {
      getSettings: "getNotificationSettings",
      setSetting: "setNotificationSetting"
    }),
    onChange(item, key) {
      this.setSetting({ key: item.key, subkey: key, value: item[`${key}`] });
    },
    toggleExpanded(key) {
      if (this.expandedItems.includes(key)) {
        this.expandedItems = this.expandedItems.filter(item => item !== key);
      } else {
        this.expandedItems.push(key);
      }
    },
    getIconName(key) {
      const icons = {
        messages: 'mdi-message-text',
        posts: 'mdi-post',
        comments: 'mdi-comment-multiple',
        needs: 'mdi-hand-heart',
        rewards: 'mdi-gift',
        follows: 'mdi-account-heart',
        newsletters: 'mdi-newspaper'
      };
      return icons[key] || 'mdi-bell';
    },
    getIconColor(key) {
      const colors = {
        messages: '#4CAF50',
        posts: '#2196F3',
        comments: '#FF9800',
        needs: '#E91E63',
        rewards: '#9C27B0',
        follows: '#FF5722',
        newsletters: '#607D8B'
      };
      return colors[key] || '#2196F3';
    }
  },
  mounted() {
    this.loading = true;
    this.getSettings()
      .then(res => {
        this.loading = false;
        res.map((r, index) => {
          this.menuList[index] = { ...this.menuList[index], ...r };
        });
      })
      .catch(error => {
        this.loading = false;
        console.log(error);
      });
  }
};
</script>

<style scoped>
.notification-settings {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  padding: 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  min-width: 100% !important;
}

/* Header Section */
.header-section {
  position: relative;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  padding: 2rem 1rem 3rem;
  overflow: hidden;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 2;
}

.header-icon {
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  padding: 1rem;
  backdrop-filter: blur(10px);
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.header-subtitle {
  font-size: 1.1rem;
  color: rgba(255,255,255,0.9);
  margin: 0.5rem 0 0;
  font-weight: 300;
}

.header-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(50%, -50%);
}

/* Loading State */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.loading-content {
  text-align: center;
  background: rgba(255,255,255,0.1);
  padding: 3rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.loading-text {
  color: white;
  font-size: 1.1rem;
  margin-top: 1rem;
  font-weight: 300;
}

/* Settings Container */
.settings-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.settings-grid {
  display: grid;
  gap: 1.5rem;
  /* grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); */
}

/* Setting Card */
.setting-card {
  background: rgba(255,255,255,0.95);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.setting-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.setting-card.expanded {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.15);
}

/* Card Header */
.card-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.card-icon {
  background: rgba(255,255,255,0.9);
  border-radius: 12px;
  padding: 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.card-title-section {
  flex: 1;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.25rem;
}

.card-description {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

.expand-indicator {
  margin-left: auto;
}

.expand-icon {
  transition: transform 0.3s ease;
  color: #6c757d;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Card Content */
.card-content {
  padding: 0 1.5rem 1.5rem;
  background: white;
}

.notification-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
}

/* Notification Option */
.notification-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.notification-option:hover {
  background: #e9ecef;
  transform: translateX(4px);
}

.option-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.option-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.option-icon.push {
  background: linear-gradient(135deg, #4CAF50, #45a049);
}

.option-icon.email {
  background: linear-gradient(135deg, #2196F3, #1976D2);
}

.option-icon.sms {
  background: linear-gradient(135deg, #FF9800, #F57C00);
}

.option-details {
  display: flex;
  flex-direction: column;
}

.option-label {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.option-sublabel {
  font-size: 0.85rem;
  color: #6c757d;
}

.option-toggle {
  margin-left: auto;
}

/* Custom Switch Styling */
.custom-switch {
  margin: 0;
}

.custom-switch >>> .v-input__slot {
  margin-bottom: 0;
}

.custom-switch >>> .v-switch__track {
  background-color: #e0e0e0 !important;
}

.custom-switch >>> .v-switch__thumb {
  background-color: white !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-title {
    font-size: 2rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  .card-content {
    padding: 0 1rem 1rem;
  }
  
  .notification-option {
    padding: 0.75rem;
  }
  
  .option-label {
    font-size: 0.9rem;
  }
  
  .option-sublabel {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .header-title {
    font-size: 1.75rem;
  }
  
  .settings-container {
    padding: 1rem;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .expand-indicator {
    align-self: flex-end;
    margin-top: -2rem;
  }
  
  .option-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .option-details {
    text-align: left;
  }
}

/* Animation for card expansion */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-content {
  animation: slideDown 0.3s ease;
}
</style>